import { useState, useCallback, useMemo, useEffect } from "react";
import { ICustomer, Customer } from "../../../entities-generated/common/customer";
import { BwjInput } from "../../../framework/entities/bwj-input";
import { BwjValidityState } from "../../../framework/entities/bwj-validity-state";
import { EventNotificationService, useEventNotification } from "../../../framework/services/event-notification";
import { ImmutableFormValueService, ValidatedFormValueService, IFormValidityCheck } from "../../../framework/services/form-model-service-components";
import { NavigationService } from "../../../framework/services/navigation";
import { contentChanged } from "../../../framework/utils";
import { none, where } from "../../../framework/utils/collection";
import { ProjectEvents } from "../events";
import { ICustomerEditorFormModel, ICustomerEditorValueModel, ICustomerEditorModelChange } from "./customer-editor-iface";
import { IUpdateCustomerRequest } from "../../../entities-generated/models/project/update-customer-request";


// ##BEGIN:DEVELOPERCODE##

class FixedValueService extends ImmutableFormValueService<ICustomer, string> {
    public initialize(data: ICustomer): void {
        const d = data as unknown as { [key:string]: string };
        this.value = d[this.id];
    }

    public applyToDataModel(data: ICustomer): void {
        const d = data as unknown as { [key:string]: string };
        d[this.id] = this.value;
    }

    public getDefaultValue(): string {
        return '';
    }

    public getInputValue(): string {
        return this.value;
    }
}

class RequiredInputService extends ValidatedFormValueService<ICustomer, string, string> {
    public initialize(data: ICustomer): void {
        const d = data as unknown as { [key:string]: string };
        this.setInputValue(new BwjInput<string>(d[this.id]));
        this.valueUntouched = true;
    }

    public applyToDataModel(data: ICustomer): void {
        const d = data as unknown as { [key:string]: string };
        d[this.id] = this.value.value;
    }

    public getDefaultValue(): BwjInput<string> {
        return new BwjInput<string>('');
    }

    public getInputValue(): BwjInput<string> {
        return this.value;
    }

    public setInputValue(value: BwjInput<string>): void {
        value.id = this.id;
        value.description = this.description;
        this.value = value;
    }

    public getOutputValue(): string {
        return this.value.value;
    }

    public validate(value: string): Promise<BwjInput<string>> {
        const result = this.requiredFieldValidator(value, `Enter customer ${this.description.toLowerCase()}`);
        return Promise.resolve(result);
    }

    public onUpdate(value: string): void {
        this.validate(value).then(result => {
            this.setInputValue(result);
            !!this.publishChange && this.publishChange(this.id); });
        this.valueUntouched = false;
    }
}

class OptionalInputService extends ValidatedFormValueService<ICustomer, string, string> {
    public initialize(data: ICustomer): void {
        const d = data as unknown as { [key:string]: string };
        this.setInputValue(new BwjInput<string>(d[this.id]));
        this.valueUntouched = true;
    }

    public applyToDataModel(data: ICustomer): void {
        const d = data as unknown as { [key:string]: string };
        d[this.id] = this.value.value;
    }

    public getDefaultValue(): BwjInput<string> {
        return new BwjInput<string>('');
    }

    public getInputValue(): BwjInput<string> {
        return this.value;
    }

    public setInputValue(value: BwjInput<string>): void {
        value.id = this.id;
        value.description = this.description;
        this.value = value;
    }

    public getOutputValue(): string {
        return this.value.value;
    }

    public validate(value: string): Promise<BwjInput<string>> {
        const result = this.getInputObject(value);
        return Promise.resolve(result);
    }

    public onUpdate(value: string): void {
        this.validate(value).then(result => {
            this.setInputValue(result);
            !!this.publishChange && this.publishChange(this.id); });
        this.valueUntouched = false;
    }

}

// ##END:DEVELOPERCODE##

// %%BEGIN:providedName/immutable/string%%
export const providedName = new FixedValueService(
    'providedName' // id
    ,'Name (provided from customer)' // description
);
// %%END:providedName/immutable/string%%

// %%BEGIN:houseNumber/immutable/string%%
export const houseNumber = new FixedValueService(
    'houseNumber' // id
    ,'House number' // description
);
// %%END:houseNumber/immutable/string%%

// %%BEGIN:state/immutable/string%%
export const state = new FixedValueService(
    'state' // id
    ,'State' // description
);
// %%END:state/immutable/string%%

// %%BEGIN:paymentProcessorCustomerId/immutable/string%%
export const paymentProcessorCustomerId = new FixedValueService(
    'paymentProcessorCustomerId' // id
    ,'Payment processor customer ID' // description
);
// %%END:paymentProcessorCustomerId/immutable/string%%

// %%BEGIN:name/validated/string/string%%
export const name = new RequiredInputService(
    'name' // id
    ,'Full name' // description
);
// %%END:name/validated/string/string%%

// %%BEGIN:firstName/validated/string/string%%
export const firstName = new RequiredInputService(
    'firstName' // id
    ,'First name' // description
);
// %%END:firstName/validated/string/string%%

// %%BEGIN:streetName/validated/string/string%%
export const streetName = new RequiredInputService(
    'streetName' // id
    ,'Street name' // description
);
// %%END:streetName/validated/string/string%%

// %%BEGIN:city/validated/string/string%%
export const city = new RequiredInputService(
    'city' // id
    ,'City' // description
);
// %%END:city/validated/string/string%%

// %%BEGIN:zipCode/validated/string/string%%
export const zipCode = new RequiredInputService(
    'zipCode' // id
    ,'Postal code' // description
);
// %%END:zipCode/validated/string/string%%

// %%BEGIN:phonePrimary/validated/string/string%%
export const phonePrimary = new RequiredInputService(
    'phonePrimary' // id
    ,'Phone number' // description
);
// %%END:phonePrimary/validated/string/string%%

// %%BEGIN:phoneSecondary/validated/string/string%%
export const phoneSecondary = new OptionalInputService(
    'phoneSecondary' // id
    ,'Alternate phone number' // description
);
// %%END:phoneSecondary/validated/string/string%%

// %%BEGIN:email/validated/string/string%%
export const email = new RequiredInputService(
    'email' // id
    ,'Email' // description
);
// %%END:email/validated/string/string%%

// %%BEGIN:notes/validated/string/string%%
export const notes = new OptionalInputService(
    'notes' // id
    ,'Notes' // description
);
// %%END:notes/validated/string/string%%

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const toFormModel = (): ICustomerEditorFormModel => {
    return {
       houseNumber: houseNumber.getInputValue(),
       name: name.getInputValue(),
       firstName: firstName.getInputValue(),
       streetName: streetName.getInputValue(),
       city: city.getInputValue(),
       zipCode: zipCode.getInputValue(),
       phonePrimary: phonePrimary.getInputValue(),
       phoneSecondary: phoneSecondary.getInputValue(),
       email: email.getInputValue(),
       notes: notes.getInputValue(),
    };
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const toValueModel = (): ICustomerEditorValueModel => {
    return {
       providedName: providedName.getOutputValue(),
       houseNumber: houseNumber.getOutputValue(),
       state: state.getOutputValue(),
       paymentProcessorCustomerId: paymentProcessorCustomerId.getOutputValue(),
       name: name.getOutputValue(),
       firstName: firstName.getOutputValue(),
       streetName: streetName.getOutputValue(),
       city: city.getOutputValue(),
       zipCode: zipCode.getOutputValue(),
       phonePrimary: phonePrimary.getOutputValue(),
       phoneSecondary: phoneSecondary.getOutputValue(),
       email: email.getOutputValue(),
       notes: notes.getOutputValue(),
    };
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
const getValidatedInputs = () => {
    return [
        name.getInputValue(),
        firstName.getInputValue(),
        streetName.getInputValue(),
        city.getInputValue(),
        zipCode.getInputValue(),
        phonePrimary.getInputValue(),
        phoneSecondary.getInputValue(),
        email.getInputValue(),
        notes.getInputValue(),
    ];
}

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const isFormValid = () => none(getValidatedInputs(), (i) => i.validity === BwjValidityState.Invalid);

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const getFormErrors = () => where(getValidatedInputs(), (i) => i.validity === BwjValidityState.Invalid);

// WARNING: GENERATED CODE -- changes will be lost on regeneration
const broadcastChange = (propertyName: string) => {
    const arg: ICustomerEditorModelChange = {
        propertyChanged: propertyName,
        model: toFormModel()
    };

    EventNotificationService.dispatch(ProjectEvents.CustomerModelDidUpdate, arg);
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const validateAndUpdateModel = (): Promise<IFormValidityCheck<ICustomerEditorFormModel>> => {
    const promises = [
        name.formValidateAndUpdate(),
        firstName.formValidateAndUpdate(),
        streetName.formValidateAndUpdate(),
        city.formValidateAndUpdate(),
        zipCode.formValidateAndUpdate(),
        phonePrimary.formValidateAndUpdate(),
        phoneSecondary.formValidateAndUpdate(),
        email.formValidateAndUpdate(),
        notes.formValidateAndUpdate(),
    ];

    return Promise.all(promises)
        .then(() => { 
            broadcastChange('validateAndUpdateModel:ICustomerEditorFormModel');
            return { isValid: isFormValid(), model: toFormModel() };
        });
}

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const isModelChanged = () => {
    if(initialState.valueModel === undefined) { return false; }
    const current = toValueModel();
    return contentChanged(initialState.valueModel, current);
}

// WARNING: GENERATED CODE -- changes will be lost on regeneration
const initialState: { valueModel?: ICustomerEditorValueModel, dataModel: ICustomer } = {
    dataModel: new Customer()
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const initializeService = (data?: ICustomer) => {
    const dataModel = data || new Customer();

    providedName.initialize(dataModel);
    houseNumber.initialize(dataModel);
    state.initialize(dataModel);
    paymentProcessorCustomerId.initialize(dataModel);
    name.initialize(dataModel);
    firstName.initialize(dataModel);
    streetName.initialize(dataModel);
    city.initialize(dataModel);
    zipCode.initialize(dataModel);
    phonePrimary.initialize(dataModel);
    phoneSecondary.initialize(dataModel);
    email.initialize(dataModel);
    notes.initialize(dataModel);

    initialState.valueModel = toValueModel();
    initialState.dataModel = dataModel;
    broadcastChange('initializeService:ICustomerEditorFormModel');
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
name.setChangePublisher(broadcastChange);
firstName.setChangePublisher(broadcastChange);
streetName.setChangePublisher(broadcastChange);
city.setChangePublisher(broadcastChange);
zipCode.setChangePublisher(broadcastChange);
phonePrimary.setChangePublisher(broadcastChange);
phoneSecondary.setChangePublisher(broadcastChange);
email.setChangePublisher(broadcastChange);
notes.setChangePublisher(broadcastChange);

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const setReversionState = () => {
    const dataModel = new Customer();

    providedName.applyToDataModel(dataModel);
    houseNumber.applyToDataModel(dataModel);
    state.applyToDataModel(dataModel);
    paymentProcessorCustomerId.applyToDataModel(dataModel);
    name.applyToDataModel(dataModel);
    firstName.applyToDataModel(dataModel);
    streetName.applyToDataModel(dataModel);
    city.applyToDataModel(dataModel);
    zipCode.applyToDataModel(dataModel);
    phonePrimary.applyToDataModel(dataModel);
    phoneSecondary.applyToDataModel(dataModel);
    email.applyToDataModel(dataModel);
    notes.applyToDataModel(dataModel);

    initialState.valueModel = toValueModel();
    initialState.dataModel = dataModel;
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const revertChanges = () => {
    initializeService(initialState.dataModel);
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const useFormModelChangeEvent = (onChangeWrappedInUseCallback: ((change: ICustomerEditorModelChange) => void), subscriberId: string) =>
    useEventNotification(onChangeWrappedInUseCallback, [onChangeWrappedInUseCallback], ProjectEvents.CustomerModelDidUpdate, subscriberId);

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const useFormModel = (subscriberId: string): ICustomerEditorFormModel => {
    const [getter, setter] = useState<ICustomerEditorFormModel>(toFormModel());

    useFormModelChangeEvent(useCallback((change: ICustomerEditorModelChange) => setter(change.model), []), subscriberId);

    return getter;
}
// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const useHasUnsavedChanges = (model: ICustomerEditorFormModel, warnOnUnsavedPageNavigation: boolean = false) => {
    const hasUnsavedChanges = useMemo(isModelChanged, [model]);

    useEffect(() => {
        if(warnOnUnsavedPageNavigation === false || hasUnsavedChanges === false) { return; }

        const unloadCallback = (event: BeforeUnloadEvent) => {
            event.preventDefault();
            event.returnValue = '';
            return '';
        };

        window.addEventListener('beforeunload', unloadCallback);
        NavigationService.registerObjection('CustomerEditorFormService');

        return () => {
            window.removeEventListener('beforeunload', unloadCallback);
            NavigationService.rescindObjection('CustomerEditorFormService');
        }
    }, [warnOnUnsavedPageNavigation, hasUnsavedChanges]);

    return hasUnsavedChanges;
}
// ##BEGIN:DEVELOPERCODE##

export const toUpdateRequest = () => {
    const v = toValueModel();
    const request: IUpdateCustomerRequest = {
        name: v.name,
        firstName: v.firstName,
        streetName: v.streetName,
        city: v.city,
        zipCode: v.zipCode,
        phonePrimary: v.phonePrimary,
        phoneSecondary: v.phoneSecondary ?? '',
        email: v.email,
        notes: v.notes,
    };
    return request;
}

export const toCustomer = () => {
    const v = toValueModel();
    const customer: ICustomer = {
        providedName: v.providedName,
        name: v.name,
        firstName: v.firstName,
        houseNumber: v.houseNumber,
        streetName: v.streetName,
        city: v.city,
        state: v.state,
        zipCode: v.zipCode,
        phonePrimary: v.phonePrimary,
        phoneSecondary: v.phoneSecondary ?? '',
        email: v.email,
        notes: v.notes,
        paymentProcessorCustomerId: v.paymentProcessorCustomerId,
    };
    return customer;
};

// ##END:DEVELOPERCODE##
